// # Database View Component
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../../../auth/state';
import SharedHeader from '../../../../components/SharedHeader';
import DashboardFooter from '../../../../components/DashboardFooter';
import './db_view.css';

// # Import step icons
import stepOneIcon from '../../../../aneo_media/mentee/one.png';
import stepTwoIcon from '../../../../aneo_media/mentee/two.png';

// # Import chapter icons
import anatomyIcon from '../../../../general/library/step1/step1_icons/anatomy.png';
import biochemistryIcon from '../../../../general/library/step1/step1_icons/biochemistry.png';
import bloodIcon from '../../../../general/library/step1/step1_icons/blood.png';
import cnsIcon from '../../../../general/library/step1/step1_icons/cns.png';
import earIcon from '../../../../general/library/step1/step1_icons/ear.png';
import endocrineIcon from '../../../../general/library/step1/step1_icons/endocrine.png';
import emergencyIcon from '../../../../general/library/step1/step1_icons/emergency.png';
import eyeIcon from '../../../../general/library/step1/step1_icons/eye.png';
import gitIcon from '../../../../general/library/step1/step1_icons/git.png';
import healthcareIcon from '../../../../general/library/step1/step1_icons/healthcare.png';
import heartIcon from '../../../../general/library/step1/step1_icons/heart.png';
import immunologyIcon from '../../../../general/library/step1/step1_icons/immunology.png';
import infectiousIcon from '../../../../general/library/step1/step1_icons/infectious.png';
import kidneyIcon from '../../../../general/library/step1/step1_icons/kidney.png';
import lungsIcon from '../../../../general/library/step1/step1_icons/lungs.png';
import musculoskeletalIcon from '../../../../general/library/step1/step1_icons/musculoskeletal.png';
import newbornIcon from '../../../../general/library/step1/step1_icons/newborn.png';
import pathologyIcon from '../../../../general/library/step1/step1_icons/pathology.png';
import pharmaIcon from '../../../../general/library/step1/step1_icons/pharma.png';
import psychiatryIcon from '../../../../general/library/step1/step1_icons/psychiatry.png';
import publicHealthIcon from '../../../../general/library/step1/step1_icons/public_health.png';
import qualityIcon from '../../../../general/library/step1/step1_icons/quality.png';
import reproductiveIcon from '../../../../general/library/step1/step1_icons/reproductive-system.png';
import skinIcon from '../../../../general/library/step1/step1_icons/skin.png';
import statisticsIcon from '../../../../general/library/step1/step1_icons/statistics.png';
import surgeryIcon from '../../../../general/library/step1/step1_icons/surgery.png';
import wellPatientIcon from '../../../../general/library/step1/step1_icons/well_patient.png';
import pregnantIcon from '../../../../general/library/step1/step1_icons/pregnant.png';

const DatabaseView = () => {
  const { step } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuthStore();

  // # Redirect if not admin
  React.useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, navigate]);

  // # Define chapters based on step
  const getChapters = () => {
    if (step === 'step1') {
      return [
        { id: 'Biochemistry', title: 'Biochemistry & Genetics', icon: biochemistryIcon },
        { id: 'Immunology', title: 'Immunology', icon: immunologyIcon },
        { id: 'Microbiology', title: 'Microbiology', icon: infectiousIcon },
        { id: 'General Pathology', title: 'General Pathology', icon: pathologyIcon },
        { id: 'Pharmacology', title: 'Pharmacology', icon: pharmaIcon },
        { id: 'Public Health', title: 'Public Health Sciences', icon: publicHealthIcon },
        { id: 'Anatomy', title: 'Anatomy & Histology', icon: anatomyIcon },
        { id: 'Cardiovascular', title: 'Cardiovascular System', icon: heartIcon },
        { id: 'Endocrine', title: 'Endocrine System', icon: endocrineIcon },
        { id: 'Gastrointestinal', title: 'Gastrointestinal System', icon: gitIcon },
        { id: 'Hematology', title: 'Hematology & Oncology', icon: bloodIcon },
        { id: 'Musculoskeletal', title: 'Musculoskeletal & Connective Tissue', icon: musculoskeletalIcon },
        { id: 'Dermatology', title: 'Dermatology', icon: skinIcon },
        { id: 'Neurology', title: 'Neurology', icon: cnsIcon },
        { id: 'Special Senses', title: 'Special Senses', icon: eyeIcon },
        { id: 'Psychiatry', title: 'Behavioral Sciences', icon: psychiatryIcon },
        { id: 'Renal', title: 'Renal System', icon: kidneyIcon },
        { id: 'Reproductive', title: 'Reproductive System', icon: reproductiveIcon },
        { id: 'Respiratory', title: 'Respiratory System', icon: lungsIcon }
      ];
    } else if (step === 'step2') {
      return [
        { id: 'Cardiovascular', title: 'Cardiovascular', icon: heartIcon },
        { id: 'Respiratory & ENT', title: 'Respiratory & ENT', icon: lungsIcon },
        { id: 'Renal & Urology', title: 'Renal & Urology', icon: kidneyIcon },
        { id: 'Endocrine', title: 'Endocrine', icon: endocrineIcon },
        { id: 'Hematology & Oncology', title: 'Hematology & Oncology', icon: bloodIcon },
        { id: 'Immunology', title: 'Immunology', icon: immunologyIcon },
        { id: 'Gastrointestinal', title: 'Gastrointestinal', icon: gitIcon },
        { id: 'Musculoskeletal', title: 'Musculoskeletal', icon: musculoskeletalIcon },
        { id: 'Neurology', title: 'Neurology', icon: cnsIcon },
        { id: 'Ophthalmology', title: 'Ophthalmology', icon: eyeIcon },
        { id: 'Psychiatry', title: 'Psychiatry', icon: psychiatryIcon },
        { id: 'Dermatology', title: 'Dermatology', icon: skinIcon },
        { id: 'Gynecology', title: 'Gynecology', icon: reproductiveIcon },
        { id: 'Obstetrics', title: 'Obstetrics', icon: pregnantIcon },
        { id: 'Pediatrics & Neonatology', title: 'Pediatrics & Neonatal Medicine', icon: newbornIcon },
        { id: 'Genetics', title: 'Genetics', icon: biochemistryIcon },
        { id: 'Infectious Disease', title: 'Infectious Disease', icon: infectiousIcon },
        { id: 'Surgery & Anesthesia', title: 'Surgery & Anesthesia', icon: surgeryIcon },
        { id: 'Emergency Medicine & Trauma', title: 'Emergency Medicine & Trauma', icon: emergencyIcon },
        { id: 'Well-Patient', title: 'Preventive Medicine', icon: wellPatientIcon },
        { id: 'Biostatistics & Epidemiology', title: 'Biostatistics & Epidemiology', icon: statisticsIcon },
        { id: 'Ethics & Communication Skills', title: 'Ethics & Communication Skills', icon: publicHealthIcon },
        { id: 'Healthcare Delivery', title: 'Healthcare Delivery', icon: healthcareIcon },
        { id: 'Quality & Safety', title: 'Quality & Safety', icon: qualityIcon },
        { id: 'Pharmacology Review', title: 'Pharmacology Review', icon: pharmaIcon }
      ];
    }
    return [];
  };

  // # Handle chapter navigation
  const handleChapterClick = (chapterId) => {
    navigate(`/admin/content/${step}/${chapterId}`);
  };

  return (
    <div className="mstr_db_vw_database-view">
      <SharedHeader />
      
      <main className="mstr_db_vw_database-content">
        <div className="mstr_db_vw_database-header">
          <button 
            className="mstr_db_vw_back-button"
            onClick={() => navigate('/admin/content')}
          >
            <i className="fas fa-arrow-left"></i> Back to Master Database
          </button>
          <h1>USMLE {step.toUpperCase()} Database</h1>
        </div>

        <div className="mstr_db_vw_database-chapters-grid">
          {getChapters().map((chapter) => (
            <div 
              key={chapter.id}
              className="mstr_db_vw_database-chapter-card"
              onClick={() => handleChapterClick(chapter.id)}
            >
              <div className="mstr_db_vw_database-chapter-icon">
                <img src={chapter.icon} alt={chapter.title} />
              </div>
              <h3>{chapter.title}</h3>
            </div>
          ))}
        </div>
      </main>

      <DashboardFooter />
    </div>
  );
};

export default DatabaseView;
